import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  styled,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import Sidebar from '../components/Sidebar';
import config from '../config';

const PageContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  alignContent: 'center',
  width: '100%',
  height: '100%',
});

const ContentContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  alignContent: 'center',
  width: '100%',
});

const ButtonWrapper = styled('div')(({ theme }) => ({
  margin: theme.spacing(2),
  marginLeft: "0",
  marginBottom: theme.spacing(5),
  marginTop: 'auto',
  display: 'flex',
  justifyContent: 'center',
}));

const CopyPomPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    const params = {
      products: products,
    };

    try {
      const response = await axios.post(`${config.serverUrl}/render/copy-pom`, null, {
        params: params,
      });

      setResult(response.data);
    } catch (error) {
      setError('Ошибка: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (type) => {
    switch (type) {
      case 'successful':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'no_model':
        return <WarningIcon color="warning" />;
      default:
        return null;
    }
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'successful':
        return 'success';
      case 'failed':
        return 'error';
      case 'no_model':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <PageContainer>
      <Sidebar navigate={navigate} />
      <ContentContainer>
        <Container maxWidth="md">
          <Box sx={{ mt: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              POM Models Copy
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Copies POM models (type 4) to new POM models (type 8) with _pom suffix for the same products
            </Typography>
            
            <form onSubmit={handleSubmit}>
              <TextField
                label="Products (IDs separated by comma)"
                value={products}
                onChange={(e) => setProducts(e.target.value)}
                fullWidth
                required
                margin="normal"
                placeholder="12345, 67890, 11111"
                helperText="Enter product IDs separated by comma"
              />
              <ButtonWrapper>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ minWidth: 200 }}
                >
                  {loading ? 'Copying...' : 'Copy POM Models'}
                </Button>
              </ButtonWrapper>
            </form>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {result && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Copy Results
                </Typography>
                
                {/* Summary */}
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Summary
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Total: ${result.summary?.total || 0}`} 
                      color="default" 
                    />
                    <Chip 
                      label={`Successful: ${result.summary?.successful_count || 0}`} 
                      color="success" 
                      icon={<CheckCircleIcon />}
                    />
                    <Chip 
                      label={`Errors: ${result.summary?.failed_count || 0}`} 
                      color="error" 
                      icon={<ErrorIcon />}
                    />
                    <Chip 
                      label={`No Model: ${result.summary?.no_model_count || 0}`} 
                      color="warning" 
                      icon={<WarningIcon />}
                    />
                  </Box>
                </Paper>

                {/* Successful copies */}
                {result.successful && result.successful.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon('successful')}
                        <Typography>
                          Successfully copied ({result.successful.length})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {result.successful.map((item, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">
                            Product ID: {item.product_id}
                          </Typography>
                          {item.copied_models && item.copied_models.map((model, modelIndex) => (
                            <Typography key={modelIndex} variant="body2" color="text.secondary">
                              Size: {model.size}, Path: {model.azure_path}
                            </Typography>
                          ))}
                          {index < result.successful.length - 1 && <Divider sx={{ mt: 1 }} />}
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Errors */}
                {result.failed && result.failed.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon('failed')}
                        <Typography>
                          Copy errors ({result.failed.length})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {result.failed.map((item, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="error">
                            Product ID: {item.product_id}
                          </Typography>
                          <Typography variant="body2" color="error">
                            {item.error}
                          </Typography>
                          {index < result.failed.length - 1 && <Divider sx={{ mt: 1 }} />}
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* No model */}
                {result.no_model && result.no_model.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon('no_model')}
                        <Typography>
                          No POM model ({result.no_model.length})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {result.no_model.map((item, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="warning.main">
                            Product ID: {item.product_id}
                          </Typography>
                          <Typography variant="body2" color="warning.main">
                            {item.error}
                          </Typography>
                          {index < result.no_model.length - 1 && <Divider sx={{ mt: 1 }} />}
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            )}
          </Box>
        </Container>
      </ContentContainer>
    </PageContainer>
  );
};

export default CopyPomPage;
